import { useEffect, useMemo, useState } from 'react';
import {
  listenToAdvisoryAnswers,
  listenToAdvisoryOrchestration,
  validateAdvisoryAnswersPath,
  validateAdvisoryOrchestrationPath,
  type AdvisoryRawDoc,
} from '../../services/advisoryService';
import type { SupervisorAnswerRunState } from './advisoryAnswerRun';

export type AdvisoryAnswerDocumentStatus = 'answered' | 'partial' | 'unanswerable' | 'failed';

/** Read-only Firestore projection. Unknown nested backend records remain opaque. */
export interface AdvisoryAnswerDocumentV01 {
  documentId: string;
  key: string;
  label: string;
  questionId: string;
  kind: string;
  order: number | null;
  readOrder: number | null;
  component: string;
  parent: string;
  status: AdvisoryAnswerDocumentStatus | 'invalid';
  answerText: string;
  brief: unknown;
  facts: unknown;
  citations: unknown;
  producedBy: unknown;
  verification: unknown;
  costs: unknown;
  parts: unknown;
  structured: unknown;
  rendering: unknown;
  createdAt: string | null;
  updatedAt: string | null;
}

const stringValue = (value: unknown): string => (typeof value === 'string' ? value : '');
const numberValue = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export function advisoryTimestampToIso(value: unknown): string | null {
  if (typeof value === 'string') return value || null;
  if (value === null || typeof value !== 'object') return null;
  const candidate = value as { toDate?: unknown; seconds?: unknown; _seconds?: unknown };
  if (typeof candidate.toDate === 'function') {
    try {
      const date = (candidate.toDate as () => unknown)();
      return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
    } catch {
      return null;
    }
  }
  const seconds = numberValue(candidate.seconds) ?? numberValue(candidate._seconds);
  return seconds === null ? null : new Date(seconds * 1000).toISOString();
}

export function mapAdvisoryAnswerDocument(raw: AdvisoryRawDoc): AdvisoryAnswerDocumentV01 {
  const { data } = raw;
  const status = stringValue(data.status);
  return {
    documentId: raw.id,
    key: stringValue(data.key),
    label: stringValue(data.label),
    questionId: stringValue(data.questionId),
    kind: stringValue(data.kind),
    order: numberValue(data.order),
    readOrder: numberValue(data.readOrder),
    component: stringValue(data.component),
    parent: stringValue(data.parent),
    status:
      status === 'answered' ||
      status === 'partial' ||
      status === 'unanswerable' ||
      status === 'failed'
        ? status
        : 'invalid',
    answerText: stringValue(data.answerText),
    brief: data.brief,
    facts: data.facts,
    citations: data.citations,
    producedBy: data.producedBy,
    verification: data.verification,
    costs: data.costs,
    parts: data.parts,
    structured: data.structured,
    rendering: data.rendering,
    createdAt: advisoryTimestampToIso(data.createdAt),
    updatedAt: advisoryTimestampToIso(data.updatedAt),
  };
}

export interface UseAdvisoryAnswersOptions {
  enabled: boolean;
  userId?: string;
  projectId?: string;
  durableRunId?: string;
  liveRun?: SupervisorAnswerRunState | null;
}

export interface UseAdvisoryAnswersResult {
  answerDocuments: readonly AdvisoryAnswerDocumentV01[];
  orchestrationDoc: Record<string, unknown> | null;
  answersErrorCode: string | null;
  orchestrationErrorCode: string | null;
  loadingAnswers: boolean;
  stoppedWriting: boolean;
  runId: string | null;
}

const EMPTY_RESULT: UseAdvisoryAnswersResult = {
  answerDocuments: [],
  orchestrationDoc: null,
  answersErrorCode: null,
  orchestrationErrorCode: null,
  loadingAnswers: false,
  stoppedWriting: false,
  runId: null,
};

const STALE_AFTER_MS = 65 * 60 * 1000;

export function advisoryRunLastLivenessMs(doc: Record<string, unknown> | null): number | null {
  if (!doc) return null;
  const heartbeat =
    doc.heartbeat !== null && typeof doc.heartbeat === 'object'
      ? (doc.heartbeat as Record<string, unknown>)
      : null;
  const candidates = [
    doc.updatedAt,
    doc.lastHeartbeatAt,
    doc.heartbeatAt,
    heartbeat?.updatedAt,
    heartbeat?.at,
  ];
  for (const candidate of candidates) {
    const iso = advisoryTimestampToIso(candidate);
    if (!iso) continue;
    const millis = Date.parse(iso);
    if (!Number.isNaN(millis)) return millis;
  }
  return null;
}

export function isAdvisoryRunStoppedWriting(
  doc: Record<string, unknown> | null,
  nowMs: number
): boolean {
  const status = stringValue(doc?.status);
  if (!['answering', 'running', 'active', 'answers_running'].includes(status)) return false;
  const lastLiveness = advisoryRunLastLivenessMs(doc);
  return lastLiveness !== null && nowMs - lastLiveness >= STALE_AFTER_MS;
}

export function useAdvisoryAnswers({
  enabled,
  userId,
  projectId,
  durableRunId,
  liveRun,
}: UseAdvisoryAnswersOptions): UseAdvisoryAnswersResult {
  const [state, setState] = useState<UseAdvisoryAnswersResult>(EMPTY_RESULT);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const runId = liveRun?.runId || durableRunId || '';
  const answersPath = useMemo(() => {
    if (!enabled || !userId || !projectId) return null;
    const supplied = validateAdvisoryAnswersPath(liveRun?.answersPath, userId, projectId);
    return supplied ?? `users/${userId}/strategic_advisory/${projectId}/answers`;
  }, [enabled, liveRun?.answersPath, projectId, userId]);
  const trackingPath = useMemo(() => {
    if (!enabled || !userId || !projectId || !runId) return null;
    const supplied = validateAdvisoryOrchestrationPath(
      liveRun?.trackingPath,
      userId,
      projectId,
      runId
    );
    return supplied ?? `users/${userId}/strategic_advisory/${projectId}/orchestration/${runId}`;
  }, [enabled, liveRun?.trackingPath, projectId, runId, userId]);

  useEffect(() => {
    if (!enabled || !answersPath) {
      setState(EMPTY_RESULT);
      return undefined;
    }
    let live = true;
    setState(previous => ({ ...previous, loadingAnswers: true, runId: runId || null }));
    const answersSubscription = listenToAdvisoryAnswers(
      answersPath,
      documents => {
        if (!live) return;
        setState(previous => ({
          ...previous,
          answerDocuments: documents.map(mapAdvisoryAnswerDocument),
          answersErrorCode: null,
          loadingAnswers: false,
        }));
      },
      errorCode => {
        if (!live) return;
        setState(previous => ({
          ...previous,
          answersErrorCode: errorCode,
          loadingAnswers: false,
        }));
      }
    );
    const runSubscription = trackingPath
      ? listenToAdvisoryOrchestration(
          trackingPath,
          document => {
            if (!live) return;
            setState(previous => ({
              ...previous,
              orchestrationDoc: document?.data ?? null,
              orchestrationErrorCode: null,
              runId: runId || null,
            }));
          },
          errorCode => {
            if (!live) return;
            setState(previous => ({ ...previous, orchestrationErrorCode: errorCode }));
          }
        )
      : () => undefined;
    const unsubscribeAnswers =
      typeof answersSubscription === 'function' ? answersSubscription : () => undefined;
    const unsubscribeRun =
      typeof runSubscription === 'function' ? runSubscription : () => undefined;
    return () => {
      live = false;
      unsubscribeAnswers();
      unsubscribeRun();
    };
  }, [answersPath, enabled, runId, trackingPath]);

  useEffect(() => {
    if (!enabled) return undefined;
    const interval = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(interval);
  }, [enabled]);

  return {
    ...state,
    stoppedWriting: isAdvisoryRunStoppedWriting(state.orchestrationDoc, nowMs),
    runId: runId || state.runId,
  };
}
